// src/components/ProtectedRoute.js
import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { db, auth } from "../firebase/firebase-config";
import {
  clearClientSession,
  getClientSession,
  isClientSessionExpired,
  markSessionActivity,
} from "../utils/sessionSecurity";

const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const [authReady, setAuthReady] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setAuthReady(true);
    });

    return () => unsub();
  }, []);

  useEffect(() => {
    if (!authReady || !currentUser) {
      return;
    }

    const session = getClientSession();

    const forceLogout = async () => {
      try {
        await signOut(auth);
      } catch (error) {
        console.error("Unable to sign out from protected route:", error);
      } finally {
        clearClientSession();
        navigate("/login", { replace: true });
      }
    };

    if (
      !session.userId ||
      !session.sessionId ||
      session.userId !== currentUser.uid ||
      isClientSessionExpired()
    ) {
      forceLogout();
      return;
    }

    markSessionActivity();
  }, [authReady, currentUser, navigate]);

  // 🔁 Watch session in Firestore for cross-device logout
  useEffect(() => {
    if (!authReady) return;

    const session = getClientSession();
    if (!session.userId || !session.sessionId) return;

    const ref = doc(db, "sessions", session.userId);

    const unsub = onSnapshot(ref, async (snap) => {
      const data = snap.data();
      if (!data) return;

      const { activeSessionId } = data;

      // If active session in Firestore != this device -> logout here
      if (activeSessionId && activeSessionId !== session.sessionId) {
        try {
          await signOut(auth);
        } catch (e) {
          console.error("Error signing out:", e);
        }

        clearClientSession();
        navigate("/login", { replace: true });
      }
    });

    return () => unsub();
  }, [authReady, navigate]);

  if (!authReady) {
    return null;
  }

  // Basic auth guard
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

export default ProtectedRoute;
